<template>
  <div>
    <div class="skill-slider-header">
      <span v-tooltip="$t(`skills.${props.score.skill}.app.description`)" class="title">{{ $t(`skills.${props.score.skill}.name`) }}</span>
      <div class="separator"></div>
      <span v-if="!isV1" class="percentage">{{Math.round(props.score.score)}}%</span>
      <span v-else class="skill-notation" :class="rank">{{ rank }}</span>
    </div>
    <div :style="{'--barShift': barShift}" class="empty-bar">
      <div :style="{left: highestPercent, width: highestDiff}" class="progress"></div>
      <div :style="{left: '0%', width: lowestPercent}" class="progress"></div>
      <div v-tooltip="$t('app.campaign.result.panel.skills.bar.caretTooltip')" :style="{left: props.score.score + '%'}" class="my-score">
        <CaretIcon style="height: 12px;"></CaretIcon>
      </div>
      <div v-tooltip="$t('app.campaign.result.panel.skills.bar.highestScoreToolip')" :style="{left: highestPercent}" class="dot">
        <HighestScoreIcon style="width: 12px;"></HighestScoreIcon>
      </div>
      <div v-tooltip="$t('app.campaign.result.panel.skills.bar.lowestScoreToolip')" :style="{left: lowestPercent}" class="dot">
        <LowestScoreIcon style="width: 12px;"></LowestScoreIcon>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.skill-slider-header {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  margin-bottom: 16px;

  .title {
    flex-grow: 0;
    color: var(--secondary-color);
    font-family: $bioSans;
    font-size: 12px;
    font-weight: 600;
  }

  .separator {
    flex-grow: 1;
    height: 0.5px;
    background: var(--secondary-color);
  }

  .percentage {
    flex-grow: 0;
    color: var(--secondary-color);
    font-family: $bioSans;
    font-size: 12px;
    font-weight: 400;
  }
}

.empty-bar {
  position: relative;
  border-radius: 13px;
  width: calc(100% - var(--barShift));
  left: var(--barShift);
  height: 8px;
  background: linear-gradient(90deg, #822312 0%, #CD5500 25%, #EDAE39 50%, #92C93A 75%, #00AF00 100%);

  .my-score {
    position: absolute;
    display: flex;
    z-index: 2;

    margin-top: -10px;
    top: 50%;
    transform: translate(-50%, -50%);

    i {
      margin: auto;
      font-size: 24px;
    }
  }

  .progress {
    height: 8px;
    position: absolute;
    border-radius: 13px;
    background: var(--white-color);
    border: 1px solid var(--secondary-color);
  }

  .dot {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid var(--secondary-color);
    background-color: var(--white-color);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
}

.skill-notation {
  margin: auto;
  font-size: 12px;
  color: $white-color;
  padding: 2.5px 10px;
  border-radius: 4px;

  &.A {
    background-color: #00AF00;
  }

  &.B {
    background-color: #92C93A;
  }

  &.C {
    background-color: #EDAE39;
  }

  &.D {
    background-color: #CD5500;
  }

  &.E {
    background-color: #822312;
  }

  &.style {
    background-color: grey;
  }
}

</style>

<script setup lang="ts">
import LowestScoreIcon from '@/assets/images/icon/campaign/result_panel/lowest_score.svg?component';
import HighestScoreIcon from '@/assets/images/icon/campaign/result_panel/highest_score.svg?component';
import CaretIcon from '@/assets/images/icon/campaign/result_panel/caret.svg?component';
import { Scores } from '@/types/results';

export interface Score {
  skill: string,
  score: number,
  rank?: string,
}


const props = defineProps<{
  score: Score,
  minMax: {
    min: Scores,
    max: Scores,
  },
  isV1: boolean,
}>();

const highest = computed(() => {
  let highestScore = (props?.minMax?.max?.[props?.score?.skill] ?? 1) * 100;
  if (highestScore > 100) highestScore = 100;
  return highestScore;
});

const lowest = computed(() => {
  let lowestScore = (props?.minMax?.min?.[props?.score?.skill] ?? 0) * 100;
  if (lowestScore < 0) lowestScore = 0;
  return lowestScore;
});

const barShift = computed(() => {
  let lowestScore = lowest.value;
  if (lowestScore === highest.value) lowestScore = 0;

  if (lowestScore < 4) return '15px';
  return '0px';
})

const highestDiff = computed(() => {
  let highestScore = highest.value;
  if (highestScore === lowest.value) highestScore = 100;
  return (100 - highestScore) + '%';
})

const highestPercent = computed(() => {
  let highestScore = highest.value;
  if (highestScore === lowest.value) highestScore = 100;
  return highestScore + '%';
});

const lowestPercent = computed(() => {
  let lowestScore = lowest.value;
  if (lowestScore === highest.value) lowestScore = 0;
  return lowestScore + '%';
});

const rank = computed(() => {
  if (props.score.score >= 0 && props.score.score < 20) return "E";
  else if (props.score.score >= 20 && props.score.score < 40) return "D";
  else if (props.score.score >= 40 && props.score.score < 60) return "C";
  else if (props.score.score >= 60 && props.score.score < 80) return "B";
  else if (props.score.score >= 80 && props.score.score <= 100) return "A";
})

</script>